import { useState, useEffect, useMemo } from 'react'
import { PageHeader } from '../components/UI'
import { get } from '../utils/api'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

export default function FunctionDiaryPage() {
  const [data, setData] = useState({ spaces: [], bookings: [], unassigned: [] })
  const [loading, setLoading] = useState(true)
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - d.getDay())
    return d.toISOString().split('T')[0]
  })
  const [days, setDays] = useState(14)

  const dates = useMemo(() => {
    const arr = []
    for (let i = 0; i < days; i++) {
      const d = new Date(startDate + 'T12:00:00')
      d.setDate(d.getDate() + i)
      arr.push(d.toISOString().split('T')[0])
    }
    return arr
  }, [startDate, days])

  const endDate = dates[dates.length - 1]

  useEffect(() => {
    setLoading(true)
    get(`/function-diary?start=${startDate}&end=${endDate}`)
      .then(setData).catch(console.error).finally(() => setLoading(false))
  }, [startDate, endDate])

  const shift = (dir) => {
    const d = new Date(startDate + 'T12:00:00')
    d.setDate(d.getDate() + (dir * days))
    setStartDate(d.toISOString().split('T')[0])
  }

  const today = () => {
    const d = new Date(); d.setDate(d.getDate() - d.getDay())
    setStartDate(d.toISOString().split('T')[0])
  }

  const getBookingsForCell = (spaceId, date) => {
    return data.bookings.filter(b => b.space_id === spaceId && b.arrival_date <= date && b.departure_date > date)
  }

  const statusColor = (s) => ({ tentative: '#f59e0b', definite: '#22c55e', cancelled: '#ef4444' }[s] || '#64748b')

  const formatDay = (d) => {
    const dt = new Date(d + 'T12:00:00')
    return { day: dt.getDate(), dow: ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][dt.getDay()], month: MONTHS[dt.getMonth()] }
  }

  const isToday = (d) => d === new Date().toISOString().split('T')[0]
  const isWeekend = (d) => { const dt = new Date(d + 'T12:00:00'); return dt.getDay() === 0 || dt.getDay() === 6 }

  return (
    <>
      <PageHeader title="Function Diary" subtitle="Meeting space availability at a glance" />

      <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
        <div className="btn-group btn-group-sm">
          <button className="btn btn-outline-secondary" onClick={() => shift(-1)}><IconChevronLeft size={16} /></button>
          <button className="btn btn-outline-secondary" onClick={today}>Today</button>
          <button className="btn btn-outline-secondary" onClick={() => shift(1)}><IconChevronRight size={16} /></button>
        </div>
        <select className="form-select form-select-sm" style={{ maxWidth: 140 }} value={days} onChange={e => setDays(parseInt(e.target.value))}>
          <option value={7}>1 Week</option>
          <option value={14}>2 Weeks</option>
          <option value={30}>1 Month</option>
        </select>
        <span className="text-secondary" style={{ fontSize: '0.85rem' }}>
          {new Date(startDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — {new Date(endDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </span>
      </div>

      {loading ? <div className="text-center py-5"><div className="spinner-border text-primary" /></div> : (
        <div className="card">
          <div className="table-responsive" style={{ maxHeight: 'calc(100vh - 260px)' }}>
            <table className="table table-bordered table-sm mb-0" style={{ fontSize: '0.75rem' }}>
              <thead>
                <tr style={{ position: 'sticky', top: 0, zIndex: 2, background: 'var(--tblr-bg-surface)' }}>
                  <th style={{ minWidth: 140, position: 'sticky', left: 0, zIndex: 3, background: 'var(--tblr-bg-surface)' }}>Space</th>
                  {dates.map(d => {
                    const f = formatDay(d)
                    return (
                      <th key={d} className="text-center" style={{
                        minWidth: 70, padding: '4px 2px',
                        background: isToday(d) ? 'rgba(32,107,196,0.15)' : isWeekend(d) ? 'rgba(0,0,0,0.04)' : 'var(--tblr-bg-surface)'
                      }}>
                        <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>{f.dow}</div>
                        <div className="fw-bold">{f.month} {f.day}</div>
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody>
                {data.spaces.map(space => (
                  <tr key={space.id}>
                    <td style={{ position: 'sticky', left: 0, zIndex: 1, background: 'var(--tblr-bg-surface)', fontWeight: 600 }}>
                      <div>{space.name}</div>
                      <div style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 400 }}>{space.sqft?.toLocaleString()} sqft</div>
                    </td>
                    {dates.map(d => {
                      const bookings = getBookingsForCell(space.id, d)
                      return (
                        <td key={d} style={{
                          padding: 2, verticalAlign: 'top',
                          background: isToday(d) ? 'rgba(32,107,196,0.08)' : isWeekend(d) ? 'rgba(0,0,0,0.02)' : undefined
                        }}>
                          {bookings.map(b => (
                            <div key={b.event_id} title={`${b.event_name}\n${b.company || ''}\n${b.contact_name || ''}\nSetup: ${b.setup_style || 'TBD'}`}
                              style={{
                                background: statusColor(b.status), color: '#fff', borderRadius: 3, padding: '1px 4px',
                                fontSize: '0.65rem', marginBottom: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'default'
                              }}>
                              {b.event_name}
                            </div>
                          ))}
                        </td>
                      )
                    })}
                  </tr>
                ))}
                {data.unassigned.length > 0 && (
                  <tr>
                    <td style={{ position: 'sticky', left: 0, zIndex: 1, background: 'var(--tblr-bg-surface)', fontWeight: 600, color: '#f59e0b' }}>Unassigned</td>
                    {dates.map(d => {
                      const events = data.unassigned.filter(e => e.arrival_date <= d && e.departure_date > d)
                      return (
                        <td key={d} style={{ padding: 2, verticalAlign: 'top' }}>
                          {events.map(e => (
                            <div key={e.event_id} style={{ background: '#f59e0b', color: '#fff', borderRadius: 3, padding: '1px 4px', fontSize: '0.65rem', marginBottom: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {e.event_name}
                            </div>
                          ))}
                        </td>
                      )
                    })}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="card-footer d-flex gap-3" style={{ fontSize: '0.75rem' }}>
            <span><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, background: '#22c55e', marginRight: 4 }} />Definite</span>
            <span><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, background: '#f59e0b', marginRight: 4 }} />Tentative</span>
            <span><span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, background: '#ef4444', marginRight: 4 }} />Cancelled</span>
          </div>
        </div>
      )}
    </>
  )
}
